import session, { ANALYTICS_SESSION_TIMEOUT } from './session'
import { hasVendorGrant } from './gdpr_consent'
import { postJson } from 'shared/javascripts/util/request'

// gtag keeps GA4's identifiers in cookies: `_ga` holds the client id (`GA1.1.<client_id>`), and one
// `_ga_<container_id>` per property holds the session id together with the timestamp of the last hit —
// `GS2.1.s<session_id>$o1$g1$t<last_hit>$…` in the current format, and
// `GS1.1.<session_id>.<hits>.<engaged>.<last_hit>.…` in the older one.
const GA_CLIENT_ID_COOKIE = /(?:^|;\s*)_ga=GA\d+\.\d+\.([\d.]+)/
const GA_SESSION_COOKIE = /(?:^|;\s*)_ga_[A-Z0-9]+=([^;]+)/
const GA_SESSION_FORMATS = [/^GS2\.\d+\.s(\d+)\$.*?\$t(\d+)/, /^GS1\.\d+\.(\d+)\.\d+\.\d+\.(\d+)/]

// Sends an event to GA4 server-side (our first-party endpoint → GA4 Measurement Protocol), so it is
// captured even when client-side GA is blocked by an ad blocker.
function trackServerSide(event: string, params: Record<string, string> = {}): void {
  if (!hasVendorGrant('Google Analytics')) return

  // `postJson`, not `up.request`: this is reached from the player's adblock detection, which runs in
  // the Inertia app too, where there is no `up`. `keepalive`, because some events are sent right before a reload.
  void postJson(
    '/client_events',
    { client_events: { event, client_id: clientId(), session_id: sessionId(), params } },
    { keepalive: true },
  )
}

function clientId(): string {
  return document.cookie.match(GA_CLIENT_ID_COOKIE)?.[1] ?? session.sessionIdentifier
}

function sessionId(): string {
  return gaSessionIdFromCookie() ?? session.analyticsSessionId
}

// The cookie lives for two years but is only ever rewritten by gtag, so once an ad blocker stops gtag
// its session id freezes at the user's last unblocked visit. Reusing that frozen id would collapse all
// the user's later visits into that one GA4 session, so we only trust a cookie that is still within
// GA4's inactivity window.
function gaSessionIdFromCookie(): string | undefined {
  const cookie = document.cookie.match(GA_SESSION_COOKIE)?.[1]
  if (!cookie) return undefined

  const match = GA_SESSION_FORMATS.map((format) => cookie.match(format)).find(Boolean)
  if (!match) return undefined

  const [, gaSessionId, lastHitSeconds] = match
  const stale = Date.now() - Number(lastHitSeconds) * 1000 > ANALYTICS_SESSION_TIMEOUT
  return stale ? undefined : gaSessionId
}

export function trackAdBlockerDetected(): void {
  trackServerSide('ad_blocker_detected')
}

// The events /client_events accepts for the ad-blocker notification; it silently drops any other.
export type AdBlockerNotificationEvent =
  | 'ad_blocker_notification_shown'
  | 'ad_blocker_notification_closed'
  | 'ad_blocker_guide_opened'
  | 'ad_blocker_guide_back'
  | 'ad_blocker_guide_switched'
  | 'ad_blocker_reload_clicked'

export function trackAdBlockerNotification(
  event: AdBlockerNotificationEvent,
  params: Record<string, string>,
): void {
  trackServerSide(event, params)
}
